import React, { FC } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  Tooltip,
} from "@mui/material";
import { GetBankTransferDto } from "../../../../../api-client";
import { formatDateUI } from "../../../../../helpers/dateFormat";
import { Trash2 } from "lucide-react";
import { Button as ShadButton } from "@/components/ui/button";

interface BankTransferTableProps {
  transactions: GetBankTransferDto[];
  onCancel: (transaction: GetBankTransferDto) => void;
}

/**
 * Bank Transfer specific table component
 * Handles the unique structure of bank transfers with allocations
 * Implements SRP by focusing only on bank transfer rendering
 * Supports cancellation pattern matching CASH/EC transactions
 */
export const BankTransferTable: FC<BankTransferTableProps> = ({ transactions, onCancel }) => {
  const headers = [
    "Transaktion-Nr.",
    "Rezept-Nr.",
    "Rezeptdatum",
    "Patient",
    "Motiv",
    "Typ",
    "Erstellt von",
    "Mandant",
    "Erstellt am",
    "Preis (€)",
    "Exportiert",
    "Rechnungnummer",
    "Originalbeleg",
    "Aktionen",
  ];

  const sortedTransactions = [...transactions].sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  return (
    <TableContainer component={Paper} className="scrollable" sx={{ maxHeight: "650px" }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {headers.map((header, idx) => (
              <TableCell
                key={idx}
                align={
                  header === "Aktionen" || header === "Exportiert"
                    ? "center"
                    : header === "Preis (€)"
                      ? "right"
                      : "left"
                }
                sx={{ fontWeight: "bold" }}
                className="table-header-cell-rechnung"
              >
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedTransactions && sortedTransactions.length > 0 ? (
            sortedTransactions.map((transaction) => {
              const isCancelled = transaction.isCancelled;
              const isCancellationTransaction = !!transaction.originalTransactionNumber;
              const isDisabled = isCancelled || isCancellationTransaction;

              return (
                <TableRow
                  key={transaction.transactionNumber}
                  sx={{
                    backgroundColor:
                      isCancelled || isCancellationTransaction
                        ? "rgba(174, 143, 143, 0.13)"
                        : "inherit",
                    opacity: isCancelled || isCancellationTransaction ? 0.7 : 1,
                  }}
                >
                  {/* Transaction Number with STORNO Badge */}
                  <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span
                        style={{
                          textDecoration: isCancelled ? "line-through" : "none",
                        }}
                      >
                        {transaction.transactionNumber}
                      </span>
                      {isCancellationTransaction && (
                        <Chip
                          label="STORNO"
                          size="small"
                          color="warning"
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      )}
                    </Box>
                  </TableCell>

                  {/* RX Number */}
                  <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
                    {transaction.rxNumber || ""}
                  </TableCell>

                  {/* RX Date */}
                  <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
                    {transaction.rxDate ? formatDateUI(transaction.rxDate) : "Keine Datum"}
                  </TableCell>

                  {/* Patient Name */}
                  <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
                    {transaction.patientName}
                  </TableCell>

                  {/* Text/Motiv */}
                  <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
                    {transaction.text}
                  </TableCell>

                  {/* Type */}
                  <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
                    {transaction.type}
                  </TableCell>

                  {/* Created By User */}
                  <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
                    {transaction.userName}
                  </TableCell>

                  {/* Account (Mandant) */}
                  <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
                    {transaction.accountName}
                  </TableCell>

                  {/* Created At */}
                  <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
                    {formatDateUI(transaction.createdAt)}
                  </TableCell>

                  {/* Amount */}
                  <TableCell align="right" sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
                    {Number(transaction.amount).toFixed(2).replace(".", ",") + " €"}
                  </TableCell>

                  {/* Exported */}
                  <TableCell align="center" sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
                    {transaction.wasExported ? "Ja" : "Nein"}
                  </TableCell>

                  {/* Rechnung Numbers (Allocations) */}
                  <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
                    {transaction.allocations && transaction.allocations.length > 0
                      ? transaction.allocations.map((alloc) => alloc.rechnungNumber).join(", ")
                      : "None"}
                  </TableCell>

                  {/* Original Transaction Number */}
                  <TableCell sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
                    {transaction.originalTransactionNumber && (
                      <Chip
                        label={transaction.originalTransactionNumber}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 11 }}
                      />
                    )}
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="center" sx={{ padding: "5px 0px 5px 5px", fontSize: 15 }}>
                    <Tooltip
                      title={
                        isCancelled
                          ? "Stornierte Überweisung kann nicht erneut storniert werden"
                          : isCancellationTransaction
                            ? "Stornierungsbuchung kann nicht storniert werden"
                            : "Überweisung stornieren"
                      }
                    >
                      <span>
                        <ShadButton
                          size="icon"
                          onClick={() => onCancel(transaction)}
                          disabled={isDisabled}
                          className="h-7 w-7 border-0 bg-transparent text-slate-500 shadow-none transition-all duration-200 hover:scale-110 hover:bg-transparent hover:text-slate-700 focus-visible:ring-0 focus-visible:ring-offset-0 active:scale-95 disabled:pointer-events-none disabled:opacity-30 [&_svg]:size-5"
                        >
                          <Trash2 className="h-4 w-4" />
                        </ShadButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={headers.length}>
                <Typography align="center">
                  Für die ausgewählten Daten wurden keine Transaktionen gefunden.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
